import { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { postMarketingCase } from '../../axios/requests'
import useEffectOnce from '../../hooks/useEffectOnce'
import { setAuthKey } from '../../redux/auth/auth.actions'
import { AUTH } from '../../redux/auth/auth.keys'
import { selectAuthKey } from '../../redux/auth/auth.selectors'
import { UI } from '../../redux/ui/ui.keys'
import { selectUiKey } from '../../redux/ui/ui.selectors'
import { Track } from '../../utils/track'
import { MarketingString, getConvertVisitorId } from '../../utils/marketing-string'

// Convert normally evaluates its experiments within ~1s. If it is slow, blocked by consent, or the
// page has no Convert at all, its ready callback never fires — so after this fallback we still send
// the marketing case (with utmPoa only). Ported from universal-ovm-fe's MarketingCase component.
const CONVERT_READY_TIMEOUT_MS = 3000

/**
 * Sends the marketing case ONCE per session, right after authentication succeeds, combining utmPoa
 * and the Convert `optimizely` update into a single `POST /marketing/case/:caseIdentifier` — mirrors
 * universal-ovm-fe's `MarketingCase` component (they used to be two racing posts here too: this
 * component replaces `UpdateUTMPoa`, which only sent utmPoa).
 *
 * Mounted once at the app root (like `<Modal/>`), so it covers every authenticated route
 * (variation, attachment, remuneration) instead of just the initial variation pages `UpdateUTMPoa`
 * used to wrap.
 *
 * Also identifies the Convert visitor id from the auth-provided marketing string, replacing the old
 * `useIdentifyConvertVisitor` hook's separate call to the unified backend — old-ovm-be now surfaces
 * its own marketingCase (see CrmService::getMarketingCase / ApiController::authenticateAction), so
 * there's no need to hit universal-ovm-fe's `/traffic-law/authenticate/{secret}` for this anymore.
 */
const MarketingCase = () => {
  const dispatch = useDispatch()
  const isAuthSuccess = useSelector(selectUiKey(UI.IS_AUTH_SUCCESS))
  const caseIdentifier = useSelector(selectAuthKey(AUTH.CASE_IDENTIFIER))
  const marketingString = useSelector(selectAuthKey(AUTH.MARKETING_STRING))
  const hasSent = useRef(false)

  useEffectOnce(() => {
    const convertVisitorId = getConvertVisitorId(marketingString)
    if (convertVisitorId) {
      dispatch(setAuthKey(AUTH.CONVERT_VISITOR_ID, convertVisitorId))
      Track.Experiment.Identify(convertVisitorId)
    }

    const send = async () => {
      if (hasSent.current) return
      hasSent.current = true
      const experiments = MarketingString.Experiments.Sync(marketingString, Track.Experiment.GetActiveExperiments())
      const { utmPoa } = MarketingString.UtmPoa.Create(marketingString)
      dispatch(setAuthKey(AUTH.UTM_POA, utmPoa))
      try {
        await postMarketingCase(caseIdentifier, { utmPoa, ...(experiments || {}) })
      } catch (error) {
        // Best-effort: a failed marketing case post must never break the flow.
      }
    }

    Track.Experiment.OnReady(() => void send())
    setTimeout(() => void send(), CONVERT_READY_TIMEOUT_MS)
  }, isAuthSuccess && !!caseIdentifier)

  return null
}

export default MarketingCase
